import {
  isGone,
  isNotFound,
  MedplumClient,
  OperationOutcomeError,
  parseReference,
} from "@medplum/core";
import type {
  Observation,
  Provenance,
  QuestionnaireResponse,
  Reference,
  Resource,
} from "@medplum/fhirtypes";

import { MEDPLUM_SEARCH_PAGE_MAX_COUNT } from "@/encounters/constants/medplum-search";
import type { LaborAmendStageMembershipChangeRecord } from "@/encounters/types/labor-amend-provenance";
import { getLaborAmendStageMembershipChangesFromProvenance } from "@/encounters/utils/labor-amend-provenance-stage-moves";
import {
  findLaborEncounterAmendCompletionProvenanceInBundle,
  getProvenanceEncounterQuestionnaireResponseVersionId,
  isLaborEncounterAmendCompletionProvenance,
} from "@/encounters/utils/labor-encounter-event-provenance";
import { joinReferenceString, resourceIdFromLocation } from "@/utils/medplum";

export interface LaborAmendProvenanceViewModel {
  provenance: Provenance;
  encounterQuestionnaireResponse?: QuestionnaireResponse;
  flowsheetQuestionnaireResponses: QuestionnaireResponse[];
  observations: Observation[];
  stageMembershipChanges: LaborAmendStageMembershipChangeRecord[];
  provenanceTargetObservationIds: Set<string>;
  provenanceTargetFlowsheetQuestionnaireResponseIds: Set<string>;
}

/**
 * Finds amend-completion Provenance for an encounter QuestionnaireResponse version.
 *
 * Walks every page of the encounter's Provenance, newest first. A match on both the encounter
 * QuestionnaireResponse id and version wins; otherwise the newest version-only match is returned.
 */
export async function fetchLaborAmendCompletionProvenanceForVersion(
  medplum: MedplumClient,
  encounterId: string,
  encounterQuestionnaireResponseVersionId: string,
  encounterQuestionnaireResponseId?: string,
): Promise<Provenance | undefined> {
  let versionOnlyMatch: Provenance | undefined;

  for await (const encounterProvenances of medplum.searchResourcePages("Provenance", {
    target: joinReferenceString("Encounter", encounterId),
    _sort: "-recorded",
    _count: MEDPLUM_SEARCH_PAGE_MAX_COUNT,
  })) {
    if (encounterQuestionnaireResponseId) {
      const amendCompletionProvenance = findLaborEncounterAmendCompletionProvenanceInBundle(
        encounterProvenances,
        encounterQuestionnaireResponseId,
        encounterQuestionnaireResponseVersionId,
      );
      if (amendCompletionProvenance) {
        return amendCompletionProvenance;
      }
    }

    versionOnlyMatch ??= encounterProvenances.find(
      (provenance) =>
        isLaborEncounterAmendCompletionProvenance(provenance) &&
        getProvenanceEncounterQuestionnaireResponseVersionId(provenance) ===
          encounterQuestionnaireResponseVersionId,
    );
    if (versionOnlyMatch && !encounterQuestionnaireResponseId) {
      return versionOnlyMatch;
    }
  }

  return versionOnlyMatch;
}

/**
 * Loads all resources referenced by amend-completion Provenance targets.
 */
export async function loadLaborAmendProvenanceViewModel(
  medplum: MedplumClient,
  provenance: Provenance,
): Promise<LaborAmendProvenanceViewModel> {
  const targets = provenance.target ?? [];
  const readResults = await Promise.all(
    targets.reduce<Promise<QuestionnaireResponse | Observation | undefined>[]>((reads, target) => {
      if (isReadableLaborAmendProvenanceTarget(target)) {
        reads.push(readLaborAmendProvenanceTarget(medplum, target));
      }
      return reads;
    }, []),
  );
  const resources = readResults.filter(
    (resource): resource is QuestionnaireResponse | Observation => resource !== undefined,
  );

  const encounterQuestionnaireResponseVersionId =
    getProvenanceEncounterQuestionnaireResponseVersionId(provenance);

  let encounterQuestionnaireResponse: QuestionnaireResponse | undefined;
  const flowsheetQuestionnaireResponses: QuestionnaireResponse[] = [];
  const observations: Observation[] = [];
  const provenanceTargetObservationIds = new Set<string>();
  const provenanceTargetFlowsheetQuestionnaireResponseIds = new Set<string>();

  for (const resource of resources) {
    if (resource.resourceType === "QuestionnaireResponse") {
      if (
        resource.meta?.versionId &&
        resource.meta.versionId === encounterQuestionnaireResponseVersionId
      ) {
        encounterQuestionnaireResponse = resource;
        continue;
      }
      flowsheetQuestionnaireResponses.push(resource);
      if (resource.id) {
        provenanceTargetFlowsheetQuestionnaireResponseIds.add(resource.id);
      }
      continue;
    }

    if (resource.resourceType === "Observation" && resource.id) {
      observations.push(resource);
      provenanceTargetObservationIds.add(resource.id);
    }
  }

  for (const target of targets) {
    const observationId = resourceIdFromLocation(target.reference, "Observation");
    if (observationId) {
      provenanceTargetObservationIds.add(observationId);
    }
    const questionnaireResponseId = resourceIdFromLocation(
      target.reference,
      "QuestionnaireResponse",
    );
    if (questionnaireResponseId) {
      provenanceTargetFlowsheetQuestionnaireResponseIds.add(questionnaireResponseId);
    }
  }

  return {
    provenance,
    encounterQuestionnaireResponse,
    flowsheetQuestionnaireResponses,
    observations,
    stageMembershipChanges: getLaborAmendStageMembershipChangesFromProvenance(provenance),
    provenanceTargetObservationIds,
    provenanceTargetFlowsheetQuestionnaireResponseIds,
  };
}

/**
 * Whether a Provenance target is a relative QuestionnaireResponse or Observation reference that
 * `readReference` can resolve. Other resource types and unparseable references are indexed only.
 *
 * @param target Provenance target reference
 * @returns true when the target should be read
 */
function isReadableLaborAmendProvenanceTarget(target: Reference): target is Reference<Resource> {
  try {
    const [resourceType] = parseReference(target);
    return resourceType === "QuestionnaireResponse" || resourceType === "Observation";
  } catch {
    return false;
  }
}

/**
 * Reads one Provenance target. A target deleted since the amendment resolves to undefined; every
 * other read failure propagates so the caller never renders a silently incomplete diff.
 *
 * @param medplum Medplum client
 * @param target relative QuestionnaireResponse or Observation reference
 * @returns the target resource, or undefined when it no longer exists
 */
async function readLaborAmendProvenanceTarget(
  medplum: MedplumClient,
  target: Reference<Resource>,
): Promise<QuestionnaireResponse | Observation | undefined> {
  try {
    const resource = await medplum.readReference(target);
    if (
      resource.resourceType === "QuestionnaireResponse" ||
      resource.resourceType === "Observation"
    ) {
      return resource;
    }
    return undefined;
  } catch (error) {
    // A soft-deleted target reads as 410 Gone; an expunged one reads as 404 Not Found.
    if (
      error instanceof OperationOutcomeError &&
      (isNotFound(error.outcome) || isGone(error.outcome))
    ) {
      return undefined;
    }
    throw error;
  }
}
